/**
 * SelectionToolbar - Toolbar flotante para acciones masivas en prefacturas
 *
 * Se muestra en la parte inferior de la pantalla cuando hay prefacturas seleccionadas.
 * Incluye contador de selección y botón para facturar múltiples prefacturas.
 */
import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { faXmark, faCircleDollar, faObjectsColumn } from '@fortawesome/pro-light-svg-icons';
import { memo, useCallback } from 'react';

interface SelectionToolbarProps {
  selectedCount: number;
  onClose: () => void;
  onFacturar?: () => void;
  onMerge?: () => void;
}

// Componente memoizado para los botones de acción
const ActionButton = memo(({ icon, label, onClick }: { icon: any; label: string; onClick: () => void }) => (
  <Button
    variant="outline"
    size="sm"
    onClick={onClick}
    className="border-card-border bg-transparent"
    data-testid={`selection-action-${label.toLowerCase()}`}
  >
    <FontAwesomeIcon icon={icon} className={`w-3 h-3 mr-2`} />
    {label}
  </Button>
));

ActionButton.displayName = 'ActionButton';

export const SelectionToolbar = memo(({ selectedCount, onClose, onFacturar, onMerge }: SelectionToolbarProps) => {
  const { t } = useTranslation('preInvoicesModule');

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleFacturar = useCallback(() => {
    if (onFacturar) {
      onFacturar();
    }
  }, [onFacturar]);

  const handleMerge = useCallback(() => {
    if (onMerge) {
      onMerge();
    }
  }, [onMerge]);

  // Si no hay elementos seleccionados, no renderizar nada
  if (selectedCount === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
      <div className="bg-card-bg border border-card-border rounded-lg p-2 shadow-lg">
        <div className="flex items-center gap-4">
          {/* Contador de seleccionados */}
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 bg-primary text-btn-text text-sm rounded-full flex items-center justify-center">
              {selectedCount}
            </span>
            <span className="text-sm font-medium">{t('selection.selected', 'Seleccionados')}</span>
            {/* Botón X para cerrar/limpiar selección */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="p-0 hover:bg-transparent ml-1"
              data-testid="clear-selection-button"
            >
              <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
            </Button>
          </div>

          {/* Botones de acción */}
          <div className="flex items-center gap-2">
            {/* Botón Combinar - Solo visible con 2+ seleccionadas */}
            {selectedCount >= 2 && onMerge && (
              <ActionButton icon={faObjectsColumn} label={t('buttons.merge', 'Combinar')} onClick={handleMerge} />
            )}
            {/* Botón Facturar */}
            <ActionButton icon={faCircleDollar} label={t('buttons.facturar', 'Facturar')} onClick={handleFacturar} />
          </div>
        </div>
      </div>
    </div>
  );
});

SelectionToolbar.displayName = 'SelectionToolbar';
